"use client"

import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { useFieldVoice } from './use-field-voice'
import { useVoiceAvailable } from './use-voice-available'
import type { VoiceInputApi } from './use-voice-input'
import type { VoiceInputButtonProps, VoiceInputState } from './voice-input-button'

export interface UseVoiceComposerOptions {
  /** Ref на raw <textarea> композера (уже привязан к элементу самим компонентом) */
  textareaRef: React.RefObject<HTMLTextAreaElement | null>
  /** Применить новое значение (обычно — существующий adjustTextareaHeight/adjustHeight: setState + auto-resize) */
  setText: (next: string) => void
}

/**
 * Общая логика голосового ввода для чатов-композеров (копилоты, комментарий инцидента):
 * кнопка слева от «отправить», вставка в позицию курсора, блокировка отправки/Enter
 * во время записи и распознавания.
 */
export function useVoiceComposer({ textareaRef, setText }: UseVoiceComposerOptions) {
  const available = useVoiceAvailable()
  const [voiceState, setVoiceState] = useState<VoiceInputState>('idle')
  const { fieldRef, insert, bindSelectionTracking } = useFieldVoice<HTMLTextAreaElement>({ onVoiceInsert: setText })
  const controlRef = useRef<VoiceInputApi>(null)
  const pendingSendRef = useRef<(() => void) | null>(null)

  useLayoutEffect(() => {
    fieldRef.current = textareaRef.current
  })

  const handleStateChange = useCallback((s: VoiceInputState) => setVoiceState(s), [])

  /** Пока идёт запись/распознавание — отправку (кнопка и Enter) блокируем */
  const voiceBusy = voiceState === 'recording' || voiceState === 'transcribing'

  /**
   * «Отправить» во время диктовки: запись останавливается, текст распознаётся и дописывается
   * в поле, затем вызывается `send` (он должен читать актуальный текст, например через ref).
   * false — голосовой ввод не активен, отправлять можно сразу.
   */
  const sendAfterVoice = useCallback((send: () => void): boolean => {
    const control = controlRef.current
    if (!control || (control.state !== 'recording' && control.state !== 'transcribing')) return false
    pendingSendRef.current = send
    if (control.state === 'recording') control.stopAndTranscribe()
    return true
  }, [])

  // Распознанный текст вставляется в том же рендере, где состояние стало idle, — к эффекту поле
  // уже обновлено. При ошибке распознавания отправку отменяем: текст пользователь увидит и решит сам.
  useEffect(() => {
    if (voiceState === 'idle' && pendingSendRef.current) {
      const send = pendingSendRef.current
      pendingSendRef.current = null
      send()
    } else if (voiceState === 'error') {
      pendingSendRef.current = null
    }
  }, [voiceState])

  const voiceButtonProps: VoiceInputButtonProps = {
    onInsert: insert,
    onStateChange: handleStateChange,
    size: 'md',
    placement: 'inline',
    controlRef,
  }

  return {
    /** Флаг включён и браузер поддерживает запись — иначе кнопку не рендерим */
    voiceAvailable: available,
    voiceState,
    voiceBusy,
    /** Идёт распознавание после «Отправить» во время записи — сообщение уйдёт само */
    voiceSendPending: voiceBusy && pendingSendRef.current !== null,
    sendAfterVoice,
    /** Пропсы для <VoiceInputButton {...voiceButtonProps} /> */
    voiceButtonProps,
    /** Обработчики textarea для запоминания позиции курсора: {...voiceTextareaProps} */
    voiceTextareaProps: bindSelectionTracking({}),
  }
}
